import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Paper,
  Badge,
  alpha,
  useTheme
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

const mockCustomers = [
  {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.j@example.com",
    phone: "+1 (555) 123-4567",
    status: "Active",
    joinDate: "2024-01-15",
    lastBooking: "2024-03-20",
    totalBookings: 12,
    totalSpent: 1250,
    location: "New York, NY",
    avatar: "",
    rating: 4.8
  },
  {
    id: 2,
    name: "Michael Brown",
    email: "michael.b@example.com",
    phone: "+1 (555) 987-6543",
    status: "Active",
    joinDate: "2024-02-10",
    lastBooking: "2024-03-21",
    totalBookings: 8,
    totalSpent: 840,
    location: "Los Angeles, CA",
    avatar: "",
    rating: 4.9
  },
  {
    id: 3,
    name: "Emily Davis",
    email: "emily.d@example.com",
    phone: "+1 (555) 456-7890",
    status: "Inactive",
    joinDate: "2024-01-20",
    lastBooking: "2024-02-15",
    totalBookings: 3,
    totalSpent: 275,
    location: "Chicago, IL",
    avatar: "",
    rating: 4.5
  },
  {
    id: 4,
    name: "David Wilson",
    email: "david.w@example.com",
    phone: "+1 (555) 234-5678",
    status: "Active",
    joinDate: "2024-03-01",
    lastBooking: "2024-03-22",
    totalBookings: 5,
    totalSpent: 520,
    location: "Miami, FL",
    avatar: "",
    rating: 4.7
  }
];

export default function Customers() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [anchorEl, setAnchorEl] = useState(null);

  const filteredCustomers = mockCustomers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedCustomers(filteredCustomers.map(c => c.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedCustomers.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedCustomers, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedCustomers.slice(1));
    } else if (selectedIndex === selectedCustomers.length - 1) {
      newSelected = newSelected.concat(selectedCustomers.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedCustomers.slice(0, selectedIndex),
        selectedCustomers.slice(selectedIndex + 1),
      );
    }
    setSelectedCustomers(newSelected);
  };

  const isSelected = (id) => selectedCustomers.indexOf(id) !== -1;

  const getStatusColor = (status) => {
    return status === 'Active' ? 'success' : 'error';
  };

  const stats = [
    { label: 'Total Customers', value: '1,024', change: '+12%' },
    { label: 'Active Customers', value: '892', change: '+8%' },
    { label: 'New This Month', value: '45', change: '+15%' },
    { label: 'Avg. Rating', value: '4.7/5', change: '+0.2' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Customers
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              px: 3,
              py: 1,
            }}
          >
            Add Customer
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage and view all customer information and booking history
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.label}
                </Typography>
                <Chip 
                  label={stat.change} 
                  size="small" 
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search customers..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
            >
              Filters
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            <IconButton>
              <RefreshIcon />
            </IconButton>

            {selectedCustomers.length > 0 && (
              <Chip 
                label={`${selectedCustomers.length} selected`} 
                color="primary" 
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedCustomers.length > 0 && selectedCustomers.length < filteredCustomers.length}
                    checked={filteredCustomers.length > 0 && selectedCustomers.length === filteredCustomers.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total Bookings</TableCell>
                <TableCell>Total Spent</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((customer) => {
                  const isItemSelected = isSelected(customer.id);
                  return (
                    <TableRow
                      key={customer.id}
                      hover
                      selected={isItemSelected}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, customer.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar src={customer.avatar}>
                            {customer.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="600">
                              {customer.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Joined {new Date(customer.joinDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{customer.email}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {customer.phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2">{customer.location}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={customer.status}
                          size="small"
                          color={getStatusColor(customer.status)}
                          icon={customer.status === 'Active' ? <ActiveIcon /> : <BlockIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="600">
                          {customer.totalBookings}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="600" color="primary">
                          ${customer.totalSpent}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StarIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
                          <Typography variant="body2" fontWeight="600">
                            {customer.rating}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCustomers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>
    </Box>
  );
}